import { tv } from "../../../variants/tv.js";
const layoutVariants = tv({
    slots: {
        root: '@container relative flex w-full justify-center antialiased',
        innerSlot: 'relative flex w-full',
        contentSlot: 'relative flex flex-col gap-8 @2xl:gap-12',
        headerSlot: 'flex flex-col gap-4',
        contentBodySlot: 'flex w-full flex-wrap',
        imageSlot: 'relative',
        splitImageSlot: 'relative aspect-3/2 w-full overflow-hidden @2xl:min-h-full',
        backgroundSlot: 'absolute inset-0',
        colorOverlaySlot: 'absolute inset-0'
    },
    variants: {
        display: {
            splitWithImage: {
                innerSlot: 'flex-col px-0 @2xl:max-w-none @2xl:flex-row @2xl:items-stretch',
                contentSlot: 'flex-1 py-8 pr-4 pl-4 @2xl:w-1/2 @2xl:max-w-160 @2xl:justify-center @2xl:py-12 @2xl:pr-8 @2xl:pl-8 @5xl:py-16 @5xl:pr-16 @5xl:pl-16',
                imageSlot: 'w-full @2xl:w-1/2 @2xl:flex-shrink-0 @2xl:self-stretch'
            },
            withImage: {
                innerSlot: 'flex-col gap-7 @2xl:flex-row @2xl:items-start @2xl:gap-0',
                contentSlot: 'flex-1 pr-0 pl-0 @2xl:w-1/2 @2xl:max-w-160 @2xl:justify-center @2xl:pr-8 @5xl:pr-16',
                imageSlot: 'w-full px-0 @2xl:sticky @2xl:top-0 @2xl:w-1/2 @2xl:flex-shrink-0 @2xl:px-0'
            },
            withImageCenter: {
                innerSlot: 'flex-col items-center gap-7 @2xl:gap-16',
                contentSlot: 'mx-auto w-full @2xl:px-8 [&>.flex]:justify-center',
                imageSlot: 'w-full max-w-320',
                headerSlot: 'mx-auto max-w-4xl text-center',
                contentBodySlot: 'justify-center'
            },
            simpleSplit: {
                contentSlot: '@2xl:flex-row @2xl:items-start @2xl:gap-16',
                headerSlot: '@2xl:w-1/2 @2xl:shrink-0',
                contentBodySlot: '@2xl:w-1/2 @2xl:justify-end'
            },
            simple: {
                contentSlot: 'w-full',
                headerSlot: '@2xl:w-1/2'
            },
            simpleCenter: {
                innerSlot: 'flex flex-col justify-center',
                contentSlot: 'items-center',
                headerSlot: 'mx-auto max-w-192 text-center',
                contentBodySlot: 'justify-center'
            }
        },
        imagePosition: {
            topToLeft: {},
            bottomToRight: {},
            bottomToLeft: {},
            topToRight: {}
        },
        emphasis: {
            regular: {
                root: 'bg-neutral-1',
                contentSlot: 'text-neutral-13'
            },
            medium: {
                root: 'bg-primary-3',
                contentSlot: 'text-primary-13'
            },
            bold: {
                root: 'bg-primary-12',
                contentSlot: 'text-primary-1'
            }
        },
        backgroundVariant: {
            solid: {},
            gradient: {},
            image: {
                root: 'overflow-hidden',
                backgroundSlot: '[&>*]:h-full [&>*]:w-full [&>*]:object-cover'
            }
        },
        panel: {
            true: {
                root: 'mx-auto max-w-[1248px] overflow-hidden rounded-xl @2xl:rounded-3xl',
                innerSlot: 'px-4 py-8 @2xl:px-8 @2xl:py-12 @5xl:px-16 @5xl:py-16'
            },
            false: {
                root: 'mx-auto max-w-[1248px] overflow-visible',
                innerSlot: 'px-4 py-8 @2xl:px-0 @2xl:py-12 @5xl:px-0 @5xl:py-16',
                backgroundSlot: 'inset-y-0 right-auto left-1/2 w-screen -translate-x-1/2',
                colorOverlaySlot: 'inset-y-0 right-auto left-1/2 w-screen -translate-x-1/2'
            },
            responsive: {
                root: 'mx-auto max-w-[1248px] overflow-visible @2xl:overflow-hidden @2xl:rounded-3xl',
                innerSlot: 'py-8 @2xl:px-8 @2xl:py-12 @5xl:px-16 @5xl:py-16',
                backgroundSlot: 'inset-y-0 right-auto left-1/2 w-screen -translate-x-1/2 @2xl:inset-0 @2xl:w-auto @2xl:translate-x-0',
                colorOverlaySlot: 'inset-y-0 right-auto left-1/2 w-screen -translate-x-1/2 @2xl:inset-0 @2xl:w-auto @2xl:translate-x-0'
            }
        },
        hasContentfulRichText: {
            true: {},
            false: {}
        }
    },
    compoundVariants: [
        {
            panel: true,
            emphasis: 'regular',
            class: {
                root: 'border border-primary-5'
            }
        },
        {
            panel: true,
            display: 'splitWithImage',
            class: {
                innerSlot: 'px-0 py-0 @2xl:px-0 @2xl:py-0 @5xl:px-0 @5xl:py-0'
            }
        },
        {
            panel: false,
            display: 'splitWithImage',
            class: {
                innerSlot: 'px-0 py-0 @2xl:px-0 @2xl:py-0 @5xl:px-0 @5xl:py-0',
                imageSlot: 'relative left-1/2 w-screen -translate-x-1/2 @2xl:static @2xl:w-auto @2xl:translate-x-0'
            }
        },
        {
            display: 'splitWithImage',
            imagePosition: 'topToLeft',
            class: {
                contentSlot: 'order-2 @2xl:mr-auto',
                imageSlot: 'order-1'
            }
        },
        {
            display: 'splitWithImage',
            imagePosition: 'bottomToRight',
            class: {
                contentSlot: 'order-1 @2xl:ml-auto',
                imageSlot: 'order-2'
            }
        },
        {
            display: 'splitWithImage',
            imagePosition: 'bottomToLeft',
            class: {
                contentSlot: 'order-1 @2xl:order-2 @2xl:mr-auto',
                imageSlot: 'order-2 @2xl:order-1'
            }
        },
        {
            display: 'splitWithImage',
            imagePosition: 'topToRight',
            class: {
                contentSlot: 'order-2 @2xl:order-1 @2xl:ml-auto',
                imageSlot: 'order-1 @2xl:order-2'
            }
        },
        {
            panel: false,
            display: 'splitWithImage',
            imagePosition: 'topToRight',
            class: {
                imageSlot: '@2xl:-mr-[calc((100vw-100%)/2)] @2xl:w-[calc(50%+(100vw-100%)/2)]',
                contentSlot: 'pr-0 pl-0 @2xl:ml-0 @2xl:pl-0 @5xl:pl-0'
            }
        },
        {
            panel: false,
            display: 'splitWithImage',
            imagePosition: 'bottomToRight',
            class: {
                imageSlot: '@2xl:-mr-[calc((100vw-100%)/2)] @2xl:w-[calc(50%+(100vw-100%)/2)]',
                contentSlot: 'pr-0 pl-0 @2xl:ml-0 @2xl:pl-0 @5xl:pl-0'
            }
        },
        {
            panel: false,
            display: 'splitWithImage',
            imagePosition: 'topToLeft',
            class: {
                imageSlot: '@2xl:-ml-[calc((100vw-100%)/2)] @2xl:w-[calc(50%+(100vw-100%)/2)]',
                contentSlot: 'pr-0 pl-0 @2xl:mr-0 @2xl:pr-0 @5xl:pr-0'
            }
        },
        {
            panel: false,
            display: 'splitWithImage',
            imagePosition: 'bottomToLeft',
            class: {
                imageSlot: '@2xl:-ml-[calc((100vw-100%)/2)] @2xl:w-[calc(50%+(100vw-100%)/2)]',
                contentSlot: 'pr-0 pl-0 @2xl:mr-0 @2xl:pr-0 @5xl:pr-0'
            }
        },
        {
            display: 'withImage',
            imagePosition: 'topToLeft',
            class: {
                contentSlot: 'order-2 @2xl:pr-0 @2xl:pl-8 @5xl:pr-0 @5xl:pl-16',
                imageSlot: 'order-1'
            }
        },
        {
            display: 'withImage',
            imagePosition: 'bottomToRight',
            class: {
                contentSlot: 'order-1 @2xl:pl-0 @5xl:pl-0',
                imageSlot: 'order-2'
            }
        },
        {
            display: 'withImage',
            imagePosition: 'bottomToLeft',
            class: {
                contentSlot: 'order-1 @2xl:order-2 @2xl:pr-0 @2xl:pl-8 @5xl:pr-0 @5xl:pl-16',
                imageSlot: 'order-2 @2xl:order-1'
            }
        },
        {
            display: 'withImage',
            imagePosition: 'topToRight',
            class: {
                contentSlot: 'order-2 @2xl:order-1 @2xl:pl-0 @5xl:pl-0',
                imageSlot: 'order-1 @2xl:order-2'
            }
        },
        {
            backgroundVariant: 'gradient',
            emphasis: 'regular',
            class: {
                root: 'bg-linear-to-b from-primary-3 to-primary-1'
            }
        },
        {
            backgroundVariant: 'gradient',
            emphasis: 'medium',
            class: {
                root: 'bg-[image:linear-gradient(rgba(255,255,255,0.75),rgba(255,255,255,0.75)),var(--background-image-mesh-listening)]'
            }
        },
        {
            backgroundVariant: 'gradient',
            emphasis: 'bold',
            class: {
                root: 'bg-radialPrimaryActive'
            }
        },
        {
            backgroundVariant: 'image',
            emphasis: 'regular',
            class: {
                root: 'bg-transparent',
                innerSlot: 'justify-center pb-0',
                colorOverlaySlot: 'bg-neutral-1/0'
            }
        },
        {
            backgroundVariant: 'image',
            emphasis: 'medium',
            class: {
                root: 'bg-transparent',
                innerSlot: 'justify-center pb-0',
                colorOverlaySlot: 'bg-neutral-1/60'
            }
        },
        {
            backgroundVariant: 'image',
            emphasis: 'bold',
            class: {
                root: 'bg-transparent',
                innerSlot: 'justify-center pb-0',
                colorOverlaySlot: 'bg-neutral-13/60 mix-blend-multiply'
            }
        },
        {
            panel: false,
            emphasis: 'regular',
            class: {
                backgroundSlot: 'bg-neutral-1'
            }
        },
        {
            panel: false,
            emphasis: 'medium',
            class: {
                backgroundSlot: 'bg-primary-3'
            }
        },
        {
            panel: false,
            emphasis: 'bold',
            class: {
                backgroundSlot: 'bg-primary-12'
            }
        },
        {
            panel: false,
            backgroundVariant: 'gradient',
            emphasis: 'regular',
            class: {
                backgroundSlot: 'bg-linear-to-b from-primary-3 to-primary-1'
            }
        },
        {
            panel: false,
            backgroundVariant: 'gradient',
            emphasis: 'medium',
            class: {
                backgroundSlot: 'bg-[image:linear-gradient(rgba(255,255,255,0.75),rgba(255,255,255,0.75)),var(--background-image-mesh-listening)]'
            }
        },
        {
            panel: false,
            backgroundVariant: 'gradient',
            emphasis: 'bold',
            class: {
                backgroundSlot: 'bg-radialPrimaryActive'
            }
        },
        {
            panel: false,
            backgroundVariant: 'image',
            class: {
                root: 'overflow-visible'
            }
        },
        {
            panel: 'responsive',
            emphasis: 'regular',
            class: {
                root: '@2xl:border @2xl:border-primary-5'
            }
        },
        {
            panel: 'responsive',
            display: 'splitWithImage',
            class: {
                innerSlot: 'px-0 py-0 @2xl:px-0 @2xl:py-0 @5xl:px-0 @5xl:py-0',
                contentSlot: 'pr-0 pl-0',
                imageSlot: 'relative left-1/2 w-screen -translate-x-1/2 @2xl:static @2xl:translate-x-0'
            }
        },
        {
            panel: 'responsive',
            emphasis: 'regular',
            class: {
                backgroundSlot: 'bg-neutral-1'
            }
        },
        {
            panel: 'responsive',
            emphasis: 'medium',
            class: {
                backgroundSlot: 'bg-primary-3'
            }
        },
        {
            panel: 'responsive',
            emphasis: 'bold',
            class: {
                backgroundSlot: 'bg-primary-12'
            }
        },
        {
            panel: 'responsive',
            backgroundVariant: 'gradient',
            emphasis: 'regular',
            class: {
                backgroundSlot: 'bg-linear-to-b from-primary-3 to-primary-1'
            }
        },
        {
            panel: 'responsive',
            backgroundVariant: 'gradient',
            emphasis: 'medium',
            class: {
                backgroundSlot: 'bg-[image:linear-gradient(rgba(255,255,255,0.75),rgba(255,255,255,0.75)),var(--background-image-mesh-listening)]'
            }
        },
        {
            panel: 'responsive',
            backgroundVariant: 'gradient',
            emphasis: 'bold',
            class: {
                backgroundSlot: 'bg-radialPrimaryActive'
            }
        },
        {
            panel: 'responsive',
            backgroundVariant: 'image',
            class: {
                root: 'overflow-visible @2xl:overflow-hidden'
            }
        },
        {
            hasContentfulRichText: true,
            class: {
                contentSlot: 'gap-4 @2xl:gap-8'
            }
        },
        {
            display: 'withImageCenter',
            hasContentfulRichText: true,
            class: {
                contentBodySlot: 'text-center [&_ol]:inline-block [&_ol]:text-left [&_ul]:inline-block [&_ul]:text-left'
            }
        },
        {
            panel: false,
            emphasis: 'regular',
            display: [
                'simple',
                'simpleCenter',
                'simpleSplit'
            ],
            class: {
                innerSlot: 'px-0 py-0 @2xl:px-0 @2xl:py-0 @5xl:px-0 @5xl:py-0'
            }
        }
    ],
    defaultVariants: {
        display: 'splitWithImage',
        imagePosition: 'topToRight',
        emphasis: 'regular',
        backgroundVariant: 'solid',
        panel: false,
        hasContentfulRichText: false
    }
});
const layoutDisplays = Object.keys(layoutVariants.variants.display);
const layoutEmphases = Object.keys(layoutVariants.variants.emphasis);
const layoutBackgroundVariants = Object.keys(layoutVariants.variants.backgroundVariant);
const layoutImagePositions = Object.keys(layoutVariants.variants.imagePosition);
export { layoutBackgroundVariants, layoutDisplays, layoutEmphases, layoutImagePositions, layoutVariants };
